import React, { useState, useEffect, useRef } from "react";
import { AddButton } from "../../../components/common/GeneralComponent";
import { fetchData, formatDateDMY } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Upload, FileText, Trash, Download } from "lucide-react";

const Attachments = ({
  grantId,
  onSectionSaved,
  isEditMode,
  showFinalSubmit,
  allFormData,
  onGrantUpdated,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [attachmentErrors, setAttachmentErrors] = useState({});
  const [pendingFiles, setPendingFiles] = useState({});

  const API_BASE_URL =
    process.env.REACT_APP_BASE_URL || "http://localhost:8000";

  const firstInvalidFieldRef = useRef(null);

  const attachmentTypes = [
    { value: "CNIC_Copy", label: "CNIC", essential: false },
    { value: "application", label: "Application", essential: false },
    { value: "due_diligence", label: "Due Diligence", essential: false },
    { value: "Grant_Agreement", label: "Grant Agreement", essential: false },
    { value: "Photos", label: "Photos (Equipment/Business Site)", essential: false },
  ];

  const sectionRequiredFields = {
    administrativeInfo: ["district_id"],
    // administrativeInfo: ["district_id", "tehsil_id", "uc_id", "village_name"],
    beneficiaryDetails: [
      "application_id",
      "company_name",
      "applicant_full_name",
      "cnic_number",
      "gender",
      "contact_number",
      "email",
      "address",
    ],
    grantDetails: [
      "business_category",
      "business_entity",
      "business_sector",
      "grant_asked_amount",
      "cost_rationalization_amount",
      "item_details",
      "ecib_clearance",
      "registration_date",
      "application_submission_date",
      "approval_date",
      "disbursement_date",
    ],
    verificationSupport: [],
    overallProgress: [],
  };

  useEffect(() => {
    if (grantId) {
      fetchAttachments();
    }
  }, [grantId]);

  const fetchAttachments = async () => {
    try {
      const response = await fetchData(
        `micro-grants/${grantId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      if (response?.data?.attachments) {
        setAttachments(response.data.attachments);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const flattenFormData = (allFormData) => {
    const flattened = {};

    Object.keys(allFormData).forEach((sectionName) => {
      const sectionData = allFormData[sectionName];

      if (sectionData && typeof sectionData === "object") {
        Object.keys(sectionData).forEach((key) => {
          const value = sectionData[key];

          // Handle files specially
          if (key.includes("_file") && value) {
            if (value instanceof File) {
              return;
            } else if (typeof value === "string") {
              // Keep string file paths
              flattened[key] = value;
            }
          } else if (
            value !== null &&
            value !== undefined &&
            value !== ""
          ) {
            flattened[key] = value;
          }
        });
      }
    });

    const defaultValues = {
      overall_status: "In progress",
      village_id: flattened.village_id || 1,
    };

    Object.keys(defaultValues).forEach((key) => {
      if (!flattened[key] && flattened[key] !== 0) {
        flattened[key] = defaultValues[key];
      }
    });

    return flattened;
  };

  const validateFiles = (attachmentType, files) => {
    const filesToCheck =
      attachmentType === "Photos" ? Array.from(files) : [files[0]];
    const validFiles = [];

    const allowedTypes = {
      Photos: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
      default: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ],
    };

    for (const file of filesToCheck) {
      if (file.size > 1 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 1MB.`);
        continue;
      }
      const validTypes = allowedTypes[attachmentType] || allowedTypes.default;
      if (!validTypes.includes(file.type)) {
        toast.error(
          `Invalid file type for ${file.name}. Please upload PDF, Word, or image files.`
        );
        continue;
      }
      validFiles.push(file);
    }

    return validFiles;
  };

  const handleFileUpload = async (attachmentType, files) => {
    if (!files || files.length === 0) return;

    const validFiles = validateFiles(attachmentType, files);
    if (validFiles.length === 0) return;

    if (!grantId) {
      setPendingFiles((prev) => ({
        ...prev,
        [attachmentType]: [
          ...(prev[attachmentType] || []),
          ...validFiles,
        ],
      }));
      toast.success(
        `${validFiles.length} file(s) selected.`
      );
      return;
    }

    setUploadingFiles((prev) => ({ ...prev, [attachmentType]: true }));
    try {
      let successfulUploads = 0;

      for (const file of validFiles) {
        const formData = new FormData();
        formData.append(attachmentType, file);
        formData.append("attachment_type", attachmentType);

        await fetchData(
          `micro-grants/${grantId}/attachments`,
          "POST",
          formData,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        successfulUploads++;
      }

      if (successfulUploads > 0) {
        toast.success(`${successfulUploads} file(s) uploaded successfully`);
        await fetchAttachments();
        if (isEditMode) {
          setHasUnsavedChanges(true);
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file(s)");
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [attachmentType]: false }));
    }
  };

  const handleDeleteAttachment = async (attachmentId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }

    try {
      await fetchData(
        `micro-grants/attachments/${Number(attachmentId)}`,
        "DELETE",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      // Updated delete toast success message
      toast.success(`Successfully deleted ${fileName}`);
      fetchAttachments();

      if (isEditMode) {
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      // Updated delete toast error message
      console.error("Error deleting attachment:", error);
      toast.error(`Failed to delete ${fileName}`);
    }
  };

  const uploadFilesForNewGrant = async (newGrantId) => {
    const types = Object.keys(pendingFiles);
    for (const attachmentType of types) {
      for (const file of pendingFiles[attachmentType]) {
        const formData = new FormData();
        formData.append(attachmentType, file);
        formData.append("attachment_type", attachmentType);
        await fetchData(
          `micro-grants/${newGrantId}/attachments`,
          "POST",
          formData,
          {},
          {},
          navigate,
          "",
          dispatch
        );
      }
    }
  };

  const validateEssentialAttachments = () => {
    setAttachmentErrors({});
    return true;
  };

  const handleUpdateAttachments = async () => {
    setLoading(true);

    // Step 1: Validate essential attachments
    if (!validateEssentialAttachments()) {
      setLoading(false);
      return;
    }

    try {
      // Fetch existing attachments if necessary (even if no new uploads)
      if (grantId) {
        await fetchAttachments();
        if (onGrantUpdated) {
          onGrantUpdated(); // Notify parent of update
        }
        toast.success("Attachments updated successfully");
        setHasUnsavedChanges(false); // Reset the unsaved changes flag
      }
    } catch (error) {
      console.error("Error updating attachments:", error);
      toast.error("Failed to update attachments section");
    } finally {
      setLoading(false);
    }
  };

  const getAttachmentsByType = (type) => {
    return attachments.filter((att) => att.attachment_type === type);
  };

  const validateSectionData = (sectionName, data) => {
    const requiredFields = sectionRequiredFields[sectionName] || [];

    if (!data || typeof data !== "object") {
      return requiredFields.length === 0;
    }

    return requiredFields.every((field) => {
      const value = data[field];
      return value !== null && value !== undefined && value !== "";
    });
  };

  const getSectionValidationStatus = () => {
    const status = {};

    Object.keys(sectionRequiredFields).forEach((sectionName) => {
      const sectionData = allFormData[sectionName];
      status[sectionName] = validateSectionData(sectionName, sectionData);
    });

    return status;
  };

  const createGrantWithAllData = async () => {
    try {
      const formData = new FormData();
      const flattenedData = flattenFormData(allFormData);

      // Add all flattened data to FormData
      Object.keys(flattenedData).forEach((key) => {
        if (
          key !== "has_milestones" &&
          !key.includes("_file") &&
          flattenedData[key] !== null &&
          flattenedData[key] !== undefined &&
          flattenedData[key] !== ""
        ) {
          formData.append(
            key,
            Array.isArray(flattenedData[key])
              ? JSON.stringify(flattenedData[key])
              : flattenedData[key]
          );
        }
      });

      // Handle the application_file from grantDetails
      const grantDetailsData = allFormData.grantDetails || {};
      if (
        grantDetailsData.application_file &&
        grantDetailsData.application_file instanceof File
      ) {
        formData.append("application_file", grantDetailsData.application_file);
      }
      
      const verificationSupportData = allFormData.verificationSupport || {};
      if (verificationSupportData.verification_report_file instanceof File) {
        formData.append(
          "verification_report_file",
          verificationSupportData.verification_report_file
        );
      }
      if (verificationSupportData.spot_check_report_file instanceof File) {
        formData.append(
          "spot_check_report_file",
          verificationSupportData.spot_check_report_file
        );
      }

      const response = await fetchData(
        "micro-grants/with-files",
        "POST",
        formData,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      const newGrantId = response?.grant?.micro_grant_id;

      if (!newGrantId) {
        throw new Error("Failed to create grant - no grant ID returned");
      }

      return newGrantId;
    } catch (error) {
      console.error("Error creating grant:", error);
      throw error;
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      // ✅ check essential attachments first
      if (!validateEssentialAttachments()) {
        setLoading(false);
        return;
      }

      // continue with section validation + submit logic...
      const validationStatus = getSectionValidationStatus();
      const invalidSections = Object.entries(validationStatus)
        .filter(([_, isValid]) => !isValid)
        .map(([section]) => section);

      if (invalidSections.length > 0) {
        const sectionLabels = {
          administrativeInfo: "Administrative Info",
          beneficiaryDetails: "Beneficiary Details",
          grantDetails: "Grant Details",
          verificationSupport: "Verification & Support",
          overallProgress: "Overall Progress",
        };

        const invalidLabels = invalidSections
          .map((section) => sectionLabels[section])
          .join(", ");
        toast.error(`Please complete the following sections: ${invalidLabels}`);
        setLoading(false);
        return;
      }

      // ✅ proceed with create/update grant
      let finalGrantId = grantId;
      if (!finalGrantId) {
        finalGrantId = await createGrantWithAllData();
        if (!finalGrantId) {
          toast.error("Failed to create grant");
          setLoading(false);
          return;
        }
        if (window.updateGrantId) {
          window.updateGrantId(finalGrantId);
        }
        await uploadFilesForNewGrant(finalGrantId);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      toast.success(
        "Micro Grant created successfully with required attachments!"
      );
      if (onSectionSaved) onSectionSaved();
    } catch (error) {
      console.error("Error finalizing grant creation:", error);
      toast.error("Error finalizing grant creation: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {attachmentTypes.map((attachmentType, typeIndex) => {
          const typeAttachments = getAttachmentsByType(attachmentType.value);
          const typePending = pendingFiles[attachmentType.value] || [];
          const fileCount = typeAttachments.length + typePending.length;
          const isUploading = uploadingFiles[attachmentType.value];
          const isPhotos = attachmentType.value === "Photos";

          return (
            <div
              key={attachmentType.value}
              className="flex flex-col rounded-lg border border-[#e5e7eb] bg-white"
            >
              {/* Header: number, name, file count and upload button */}
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#f3f4f6] bg-[#f9fafb] rounded-t-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#137e81] text-white text-xs font-semibold flex items-center justify-center">
                    {typeIndex + 1}
                  </span>
                  <h3 className="font-medium text-[#374151] truncate" title={attachmentType.label}>
                    {attachmentType.label}
                  </h3>
                  {fileCount > 0 && (
                    <span className="flex-shrink-0 text-xs text-[#6b7280]">({fileCount})</span>
                  )}
                  {attachmentType.essential && (
                    <span className="flex-shrink-0 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded">
                      Essential
                    </span>
                  )}
                </div>
                <label
                  htmlFor={`file-${attachmentType.value}`}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#137e81] text-white rounded-md cursor-pointer hover:bg-[#0f6366] ${
                    isUploading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Upload size={14} />
                  <span>{isUploading ? "Uploading..." : "Upload"}</span>
                </label>
                <input
                  id={`file-${attachmentType.value}`}
                  type="file"
                  multiple={isPhotos}
                  accept={isPhotos ? "image/*" : ".pdf,.doc,.docx,image/*"}
                  onChange={(e) => {
                    handleFileUpload(attachmentType.value, e.target.files);

                    e.target.value = "";
                  }}
                  disabled={isUploading}
                  className="hidden"
                />
              </div>

              {/* Files */}
              <div className="flex-1 p-3 space-y-2">
                {fileCount > 0 ? (
                  <>
                    {typeAttachments.map((attachment) => (
                      <div
                        key={attachment.attachment_id}
                        className="flex items-center justify-between gap-2 p-2 bg-[#f9fafb] rounded border"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText size={18} className="text-[#6b7280] flex-shrink-0" />
                          <div className="min-w-0">
                            <p
                              className="text-sm font-medium text-[#374151] truncate"
                              title={attachment.file_name}
                            >
                              {attachment.file_name}
                            </p>
                            <p className="text-xs text-[#6b7280]">
                              Uploaded: {formatDateDMY(attachment.uploaded_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                `${API_BASE_URL}${attachment.file_path}`,
                                "_blank"
                              )
                            }
                            className="text-blue-600 hover:text-blue-800"
                            title="View/Download"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteAttachment(
                                attachment.attachment_id,
                                attachment.file_name
                              )
                            }
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {typePending.map((file, idx) => (
                      <div
                        key={`pending-${idx}`}
                        className="flex items-center justify-between gap-2 p-2 bg-yellow-50 rounded border border-yellow-200"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText size={18} className="text-yellow-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p
                              className="text-sm font-medium text-[#374151] truncate"
                              title={file.name}
                            >
                              {file.name}
                            </p>
                            <p className="text-xs text-yellow-600">
                              Pending — will be uploaded on submit
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingFiles((prev) => ({
                              ...prev,
                              [attachmentType.value]: prev[
                                attachmentType.value
                              ].filter((_, i) => i !== idx),
                            }))
                          }
                          className="text-red-600 hover:text-red-800 flex-shrink-0"
                          title="Remove"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-[#9ca3af]">
                    <FileText size={18} className="opacity-60" />
                    <span>No files uploaded yet</span>
                  </div>
                )}
              </div>

              {attachmentErrors[attachmentType.value] && (
                <p
                  className="px-4 pb-1 text-red-500 text-sm"
                  id={attachmentType.value}
                >
                  {attachmentErrors[attachmentType.value]}
                </p>
              )}

              <p className="px-4 pb-3 text-xs text-[#9ca3af]">
                {isPhotos
                  ? "JPG, PNG, GIF · max 1MB each · multiple files allowed"
                  : "PDF, DOC, DOCX or image · max 1MB"}
              </p>
            </div>
          );
        })}
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {isEditMode && grantId && (
          <AddButton
            text="Update Attachments"
            type="button"
            onClick={handleUpdateAttachments}
            isLoading={loading}
            disabled={loading}
          />
        )}

        {showFinalSubmit && !isEditMode && (
          <AddButton
            text="Complete Grant Creation"
            type="button"
            onClick={handleFinalSubmit}
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
};

export default Attachments;
